import { z } from "zod";

/**
 * Apps theming.
 *
 * Every App renders on the `@erp/ui` design system, which reads its colors and
 * radius from CSS custom properties (`--primary`, `--border`, …) declared in
 * `@erp/ui/styles.css`. A theme is a set of overrides for those properties,
 * scoped to the App's root element, so an App can be re-skinned without
 * touching a single component.
 *
 * The Gently theme overrides nothing: it *is* the stylesheet. A brand theme
 * (generated later from an org's Template Blocks brand kit) overrides a subset.
 * Tokens not overridden fall through to Gently, so a partial brand theme still
 * renders as a coherent Gently surface.
 */

export const APP_THEME_TOKENS = [
  "background",
  "foreground",
  "card",
  "card-foreground",
  "popover",
  "popover-foreground",
  "primary",
  "primary-foreground",
  "secondary",
  "secondary-foreground",
  "muted",
  "muted-foreground",
  "accent",
  "accent-foreground",
  "destructive",
  "border",
  "input",
  "ring",
  "agent",
  "agent-foreground",
  "agent-subtle",
  "agent-border",
  "radius",
] as const;

export const AppThemeTokenSchema = z.enum(APP_THEME_TOKENS);
export type AppThemeToken = z.infer<typeof AppThemeTokenSchema>;

/**
 * A CSS value safe to interpolate into a declaration. Theme values may one day
 * come from a customer's brand kit, so anything that could close the
 * declaration or the rule (`;`, `{`, `}`), open a comment, or pull a remote
 * resource (`url(`, `@import`) is rejected rather than escaped.
 */
const CssValueSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .refine((value) => !/[;{}<>\\]|\/\*|url\(|@import/i.test(value), {
    message: "Theme values must be plain CSS values (colors, lengths, var()).",
  });

const TokenOverridesSchema = z.partialRecord(
  AppThemeTokenSchema,
  CssValueSchema,
);

export const AppThemeSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,63}$/, "Theme ids are lowercase kebab-case."),
  label: z.string().min(1).max(80),
  /** Overrides applied in light mode (and in dark mode unless `dark` overrides them). */
  light: TokenOverridesSchema.default({}),
  /** Overrides applied when the host shell is in dark mode (`.dark` ancestor). */
  dark: TokenOverridesSchema.default({}),
});
export type AppTheme = z.infer<typeof AppThemeSchema>;

/** The default theme. Empty on purpose: the `@erp/ui` stylesheet is the Gently theme. */
export const gentlyTheme: AppTheme = {
  id: "gently",
  label: "Gently",
  light: {},
  dark: {},
};

function declarations(overrides: AppTheme["light"]): string {
  return Object.entries(overrides)
    .map(([token, value]) => `--${token}: ${value};`)
    .join(" ");
}

const SCOPE = /^[\w-]{1,128}$/;

/**
 * Compile a theme into a stylesheet scoped to `[data-app-scope="<scope>"]`.
 * Each `AppView` passes its own scope, so two views sharing a theme id but
 * not its overrides never restyle each other; the scope defaults to the id.
 * Returns an empty string for a theme with no overrides (Gently), so the
 * renderer can skip emitting a `<style>` tag at all.
 *
 * The theme is re-validated here so a value that bypassed parsing upstream
 * (e.g. a DB row read without the schema) can never reach the stylesheet.
 */
export function compileAppThemeCss(
  input: AppTheme,
  scopeId: string = input.id,
): string {
  const theme = AppThemeSchema.parse(input);
  if (!SCOPE.test(scopeId)) throw new Error(`Invalid App scope "${scopeId}"`);
  const scope = `[data-app-scope="${scopeId}"]`;
  const rules: string[] = [];
  const light = declarations(theme.light);
  if (light) rules.push(`${scope} { ${light} }`);
  const dark = declarations(theme.dark);
  if (dark) rules.push(`.dark ${scope} { ${dark} }`);
  return rules.join("\n");
}
